import { DownloadIcon } from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { PermissionDecision } from "@/components/shell/PermissionGate";

export interface ExportFormat {
  key: string;
  label: string;
  /** True for an export that WRITES somewhere (a scheduled send, a push back). */
  requiresEdit?: boolean;
}

interface ExportMenuProps {
  formats: ExportFormat[];
  onExport: (formatKey: string) => void;
  decision: PermissionDecision | null | undefined;
}

/**
 * Permission-aware export (C3, I-7).
 *
 * The distinction it encodes: **read-only is not no-access.** Someone with
 * view rights may read, copy and export — taking a copy of what they can
 * already see changes nothing (UX S5). What they may not do is an export that
 * writes somewhere, and those formats are absent rather than disabled, exactly
 * as unentitled areas are absent from the rail (BR-3).
 */
export function ExportMenu({ formats, onExport, decision }: ExportMenuProps) {
  if (decision !== "edit" && decision !== "view") return null;

  const available = formats.filter((f) => decision === "edit" || !f.requiresEdit);
  if (available.length === 0) return null;

  return (
    <DropdownMenu>
      {/* data-nonmutating marks this as reachable inside a read-only
          PermissionGate, which otherwise makes every button inert. Exporting
          changes nothing, so it is not an edit control. */}
      <DropdownMenuTrigger
        data-nonmutating
        className="flex min-h-11 items-center gap-2 px-3 text-sm text-ks-ink"
      >
        <DownloadIcon className="size-4" aria-hidden />
        Export
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {available.map((format) => (
          <DropdownMenuItem key={format.key} onSelect={() => onExport(format.key)}>
            {format.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
